import { ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { PlayerRegistryEntry, PlayerRole } from '../../../models/team';
import { PlayerRegistryService } from '../../../services/player-registry.service';

@Component({
  standalone: false,
  selector: 'app-player-registry-admin',
  template: "<app-loading-overlay [show]=\"isSaving || isImporting || deletingPlayerIds.size > 0\"></app-loading-overlay>\n\n<details class=\"card accordion-card\" open>\n  <summary>\n    <span>Anagrafica giocatori</span>\n    <small>{{ filteredPlayers.length }} di {{ players.length }} censiti</small>\n  </summary>\n\n  <div class=\"accordion-body\">\n    <div class=\"section-actions registry-actions\">\n      <button type=\"button\" (click)=\"openCreatePlayerModal()\" [disabled]=\"isSaving || isImporting\">Aggiungi giocatore</button>\n      <a class=\"template-button\" href=\"/templates/anagrafica_giocatori_template.xlsx\" download>Scarica template XLSX</a>\n      <label class=\"import-button\">\n        Import XLS/CSV\n        <input type=\"file\" accept=\".xlsx,.xls,.csv,.tsv,.txt\" (change)=\"importPlayers($event)\" [disabled]=\"isImporting\" />\n      </label>\n    </div>\n    <p class=\"import-help\">\n      Per importare usa il template: compila il foglio <strong>Giocatori</strong> lasciandolo come primo foglio. Sono obbligatori\n      <strong>nome</strong>, <strong>cognome</strong>, <strong>nickname</strong> e <strong>ruolo</strong>.\n      I ruoli ammessi sono Portiere, Difensore, Centrocampista e Attaccante. Il campo opzionale <strong>eta</strong>\n      deve contenere un numero intero da 0 a 120.\n    </p>\n\n    <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n    <p class=\"success-message\" *ngIf=\"successMessage\">{{ successMessage }}</p>\n    <p class=\"success-message\" *ngIf=\"importMessage\">{{ importMessage }}</p>\n    <p class=\"loading-message\" *ngIf=\"isSaving || isImporting || deletingPlayerIds.size\">Operazione in corso, attendere...</p>\n    <div class=\"import-errors\" *ngIf=\"importErrors.length\">\n      <strong>Righe non importate</strong>\n      <small *ngFor=\"let item of importErrors\">{{ item }}</small>\n    </div>\n\n    <div class=\"registry-filters\">\n      <label>\n        Nome\n        <input type=\"search\" placeholder=\"Cerca nome\" [(ngModel)]=\"filters.firstName\" name=\"registryFilterFirstName\" (ngModelChange)=\"onFiltersChange()\" />\n      </label>\n      <label>\n        Cognome\n        <input type=\"search\" placeholder=\"Cerca cognome\" [(ngModel)]=\"filters.lastName\" name=\"registryFilterLastName\" (ngModelChange)=\"onFiltersChange()\" />\n      </label>\n      <label>\n        Nickname\n        <input type=\"search\" placeholder=\"Cerca nickname\" [(ngModel)]=\"filters.nickname\" name=\"registryFilterNickname\" (ngModelChange)=\"onFiltersChange()\" />\n      </label>\n      <button type=\"button\" class=\"secondary\" (click)=\"clearFilters()\">Pulisci filtri</button>\n    </div>\n\n    <div class=\"table-shell\">\n      <table class=\"registry-table\">\n        <thead>\n          <tr>\n            <th>Nome</th>\n            <th>Cognome</th>\n            <th>Nickname</th>\n            <th>Maglia</th>\n            <th>Ruolo</th>\n            <th>Età</th>\n            <th>Contatti</th>\n            <th>Azioni</th>\n          </tr>\n        </thead>\n        <tbody>\n          <tr *ngFor=\"let player of paginatedPlayers\">\n            <td data-label=\"Nome\">{{ player.first_name }}</td>\n            <td data-label=\"Cognome\">{{ player.last_name }}</td>\n            <td data-label=\"Nickname\"><strong>{{ player.nickname }}</strong></td>\n            <td data-label=\"Maglia\">{{ player.jersey_number || '-' }}</td>\n            <td data-label=\"Ruolo\">{{ roleLabel(player.role) }}</td>\n            <td data-label=\"Età\">{{ player.age ?? '-' }}</td>\n            <td data-label=\"Contatti\">{{ contactLabel(player) || '-' }}</td>\n            <td data-label=\"Azioni\">\n              <div class=\"row-actions\">\n                <button class=\"secondary\" type=\"button\" (click)=\"editPlayer(player)\" [disabled]=\"isSaving || isDeletingPlayer(player.id)\">Modifica</button>\n                <button class=\"danger\" type=\"button\" (click)=\"deletePlayer(player)\" [disabled]=\"isDeletingPlayer(player.id)\">\n                  {{ isDeletingPlayer(player.id) ? 'Cancellazione...' : 'Elimina' }}\n                </button>\n              </div>\n            </td>\n          </tr>\n          <tr *ngIf=\"!paginatedPlayers.length\">\n            <td colspan=\"8\" class=\"empty-table\">Nessun giocatore trovato.</td>\n          </tr>\n        </tbody>\n      </table>\n    </div>\n\n    <div class=\"pagination-bar\">\n      <span>{{ pageStart }}-{{ pageEnd }} di {{ filteredPlayers.length }}</span>\n      <label>\n        Righe\n        <select [(ngModel)]=\"pageSize\" name=\"registryPageSize\" (ngModelChange)=\"changePageSize()\">\n          <option [ngValue]=\"5\">5</option>\n          <option [ngValue]=\"10\">10</option>\n          <option [ngValue]=\"25\">25</option>\n          <option [ngValue]=\"50\">50</option>\n        </select>\n      </label>\n      <div class=\"page-actions\">\n        <button type=\"button\" class=\"secondary\" (click)=\"changePage(visiblePage - 1)\" [disabled]=\"visiblePage <= 1\">Precedente</button>\n        <span>Pagina {{ visiblePage }} di {{ totalPages }}</span>\n        <button type=\"button\" class=\"secondary\" (click)=\"changePage(visiblePage + 1)\" [disabled]=\"visiblePage >= totalPages\">Successiva</button>\n      </div>\n    </div>\n  </div>\n</details>\n\n<div class=\"modal-backdrop\" *ngIf=\"playerModalOpen\">\n  <section class=\"modal-panel\">\n    <div class=\"modal-heading\">\n      <h2>{{ editingPlayer ? 'Modifica giocatore' : 'Nuovo giocatore' }}</h2>\n      <button type=\"button\" class=\"secondary icon-button\" (click)=\"closePlayerModal()\">Chiudi</button>\n    </div>\n\n    <form class=\"form-grid\" (submit)=\"$event.preventDefault(); savePlayer();\">\n      <label>\n        Nome\n        <input type=\"text\" placeholder=\"Nome\" [(ngModel)]=\"playerForm.first_name\" name=\"playerFirstName\" />\n      </label>\n\n      <label>\n        Cognome\n        <input type=\"text\" placeholder=\"Cognome\" [(ngModel)]=\"playerForm.last_name\" name=\"playerLastName\" />\n      </label>\n\n      <label>\n        Nickname\n        <input type=\"text\" placeholder=\"Nickname\" [(ngModel)]=\"playerForm.nickname\" name=\"playerNickname\" />\n      </label>\n\n      <label>\n        Numero maglia\n        <input type=\"text\" inputmode=\"numeric\" pattern=\"[0-9]{1,3}\" placeholder=\"Numero maglia\" [(ngModel)]=\"playerForm.jersey_number\" name=\"playerJerseyNumber\" />\n      </label>\n\n      <label>\n        Ruolo\n        <select [(ngModel)]=\"playerForm.role\" name=\"playerRole\">\n          <option *ngFor=\"let role of roleOptions\" [ngValue]=\"role\">{{ role }}</option>\n        </select>\n      </label>\n\n      <label>\n        Età\n        <input type=\"number\" min=\"0\" max=\"120\" step=\"1\" inputmode=\"numeric\" placeholder=\"Età\" [(ngModel)]=\"playerForm.age\" name=\"playerAge\" />\n      </label>\n\n      <label>\n        Telefono\n        <input type=\"tel\" placeholder=\"Telefono\" [(ngModel)]=\"playerForm.phone\" name=\"playerPhone\" />\n      </label>\n\n      <label>\n        Email\n        <input type=\"email\" placeholder=\"Email\" [(ngModel)]=\"playerForm.email\" name=\"playerEmail\" />\n      </label>\n\n      <p class=\"error-message\" *ngIf=\"errorMessage\">{{ errorMessage }}</p>\n      <p class=\"loading-message\" *ngIf=\"isSaving\">Salvataggio in corso...</p>\n\n      <div class=\"actions\">\n        <button type=\"submit\" [disabled]=\"isSaving\">{{ isSaving ? 'Salvataggio...' : (editingPlayer ? 'Aggiorna giocatore' : 'Aggiungi giocatore') }}</button>\n        <button type=\"button\" class=\"secondary\" (click)=\"closePlayerModal()\" [disabled]=\"isSaving\">Annulla</button>\n      </div>\n    </form>\n  </section>\n</div>\n",
  styles: [".registry-actions {\n  align-items: center;\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.75rem;\n}\n\n.import-button,\n.template-button {\n  background: rgba(255, 255, 255, 0.08);\n  border: 1px solid rgba(255, 255, 255, 0.16);\n  border-radius: 8px;\n  color: #f8fafc;\n  cursor: pointer;\n  display: inline-flex;\n  font-weight: 700;\n  padding: 0.75rem 1rem;\n}\n\n.template-button {\n  background: rgba(34, 197, 94, 0.16);\n  border-color: rgba(74, 222, 128, 0.32);\n  color: #dcfce7;\n  text-decoration: none;\n}\n\n.import-button input {\n  display: none;\n}\n\n.import-help {\n  color: #cbd5e1;\n  line-height: 1.5;\n  margin: 0.8rem 0 0;\n}\n\n.import-help strong {\n  color: #f8fafc;\n}\n\n.import-errors {\n  background: rgba(245, 158, 11, 0.14);\n  border: 1px solid rgba(251, 191, 36, 0.35);\n  border-radius: 8px;\n  color: #fde68a;\n  display: grid;\n  gap: 0.35rem;\n  margin: 0.75rem 0;\n  padding: 0.9rem 1rem;\n}\n\n.registry-filters {\n  align-items: end;\n  display: grid;\n  gap: 0.75rem;\n  grid-template-columns: repeat(3, minmax(0, 1fr)) auto;\n  margin: 1rem 0;\n}\n\n.registry-filters label,\n.pagination-bar label {\n  display: grid;\n  gap: 0.35rem;\n}\n\n.table-shell {\n  overflow-x: auto;\n}\n\n.registry-table {\n  border-collapse: collapse;\n  min-width: 900px;\n  width: 100%;\n}\n\n.registry-table th,\n.registry-table td {\n  border-top: 1px solid rgba(255, 255, 255, 0.08);\n  padding: 0.75rem;\n  text-align: left;\n  vertical-align: middle;\n}\n\n.registry-table th {\n  color: #b9f6c8;\n  font-size: 0.8rem;\n  text-transform: uppercase;\n}\n\n.row-actions,\n.page-actions,\n.pagination-bar {\n  align-items: center;\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.55rem;\n}\n\n.row-actions {\n  justify-content: flex-end;\n}\n\n.pagination-bar {\n  justify-content: space-between;\n  margin-top: 1rem;\n}\n\n.empty-table {\n  color: #cbd5e1;\n  text-align: center;\n}\n\n.actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 0.6rem;\n}\n\n@media (max-width: 900px) {\n  .registry-filters {\n    grid-template-columns: 1fr;\n  }\n\n  .table-shell {\n    overflow-x: visible;\n  }\n\n  .registry-table {\n    min-width: 0;\n  }\n\n  .registry-table thead {\n    display: none;\n  }\n\n  .registry-table,\n  .registry-table tbody,\n  .registry-table tr,\n  .registry-table td {\n    display: block;\n    width: 100%;\n  }\n\n  .registry-table tr {\n    border-top: 1px solid rgba(255, 255, 255, 0.08);\n    padding: 0.7rem 0;\n  }\n\n  .registry-table td {\n    align-items: start;\n    border-top: 0;\n    display: grid;\n    gap: 0.75rem;\n    grid-template-columns: 7rem minmax(0, 1fr);\n    padding: 0.35rem 0;\n  }\n\n  .registry-table td::before {\n    color: #b9f6c8;\n    content: attr(data-label);\n    font-size: 0.78rem;\n    font-weight: 800;\n    text-transform: uppercase;\n  }\n\n  .registry-table .empty-table {\n    display: block;\n    padding: 1rem 0;\n  }\n\n  .registry-table .empty-table::before {\n    content: '';\n  }\n\n  .row-actions {\n    justify-content: flex-start;\n  }\n}\n"]
})
export class PlayerRegistryAdminComponent {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly playerRegistryService = new PlayerRegistryService();

  @Input() players: PlayerRegistryEntry[] = [];
  @Output() changed = new EventEmitter<void>();

  readonly roleOptions: PlayerRole[] = ['Portiere', 'Difensore', 'Centrocampista', 'Attaccante'];

  playerForm: Partial<PlayerRegistryEntry> = this.emptyPlayerForm();
  editingPlayer: PlayerRegistryEntry | null = null;
  playerModalOpen = false;
  errorMessage = '';
  successMessage = '';
  importMessage = '';
  importErrors: string[] = [];
  isSaving = false;
  isImporting = false;
  deletingPlayerIds = new Set<number>();
  filters = {
    firstName: '',
    lastName: '',
    nickname: ''
  };
  currentPage = 1;
  pageSize = 10;

  get filteredPlayers(): PlayerRegistryEntry[] {
    const firstName = this.normalizeFilter(this.filters.firstName);
    const lastName = this.normalizeFilter(this.filters.lastName);
    const nickname = this.normalizeFilter(this.filters.nickname);

    return this.players.filter((player) => {
      const matchesFirstName = !firstName || this.normalizeFilter(player.first_name).includes(firstName);
      const matchesLastName = !lastName || this.normalizeFilter(player.last_name).includes(lastName);
      const matchesNickname = !nickname || this.normalizeFilter(player.nickname).includes(nickname);
      return matchesFirstName && matchesLastName && matchesNickname;
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredPlayers.length / this.pageSize));
  }

  get visiblePage(): number {
    return Math.min(this.currentPage, this.totalPages);
  }

  get paginatedPlayers(): PlayerRegistryEntry[] {
    const start = (this.visiblePage - 1) * this.pageSize;
    return this.filteredPlayers.slice(start, start + this.pageSize);
  }

  get pageStart(): number {
    if (!this.filteredPlayers.length) {
      return 0;
    }
    return (this.visiblePage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.visiblePage * this.pageSize, this.filteredPlayers.length);
  }

  async savePlayer(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    if (!this.playerForm.first_name?.trim() || !this.playerForm.last_name?.trim() || !this.playerForm.nickname?.trim() || !this.isValidRole(this.playerForm.role)) {
      this.errorMessage = 'Nome, cognome, nickname e ruolo sono obbligatori.';
      return;
    }

    try {
      this.isSaving = true;
      this.errorMessage = '';
      this.successMessage = '';
      const payload = {
        first_name: this.playerForm.first_name.trim(),
        last_name: this.playerForm.last_name.trim(),
        nickname: this.playerForm.nickname.trim(),
        jersey_number: this.playerForm.jersey_number?.trim() || null,
        role: this.playerForm.role as PlayerRole,
        age: this.playerForm.age ?? null,
        phone: this.playerForm.phone?.trim() || null,
        email: this.playerForm.email?.trim() || null
      };

      if (this.editingPlayer) {
        await this.playerRegistryService.update(this.editingPlayer.id, payload);
      } else {
        await this.playerRegistryService.create(payload);
      }

      this.playerModalOpen = false;
      this.successMessage = 'Operazione conclusa. Giocatore salvato.';
      this.resetPlayerForm();
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante il salvataggio del giocatore.');
      this.changeDetector.detectChanges();
    } finally {
      this.isSaving = false;
      this.changeDetector.detectChanges();
    }
  }

  editPlayer(player: PlayerRegistryEntry): void {
    this.editingPlayer = player;
    this.playerForm = {
      first_name: player.first_name,
      last_name: player.last_name,
      nickname: player.nickname,
      jersey_number: player.jersey_number ?? null,
      role: player.role,
      age: player.age ?? null,
      phone: player.phone ?? null,
      email: player.email ?? null
    };
    this.playerModalOpen = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.changeDetector.detectChanges();
  }

  async deletePlayer(player: PlayerRegistryEntry): Promise<void> {
    if (this.deletingPlayerIds.has(player.id)) {
      return;
    }

    if (!window.confirm(`Confermi la cancellazione di "${this.playerLabel(player)}"?`)) {
      return;
    }

    try {
      this.deletingPlayerIds.add(player.id);
      this.errorMessage = '';
      this.successMessage = '';
      await this.playerRegistryService.delete(player.id);
      this.players = this.players.filter((item) => item.id !== player.id);
      this.normalizeCurrentPage();
      this.successMessage = 'Operazione conclusa. Giocatore cancellato.';
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.successMessage = '';
      this.errorMessage = this.readError(error, 'Errore durante la cancellazione del giocatore.');
      this.changeDetector.detectChanges();
    } finally {
      this.deletingPlayerIds.delete(player.id);
      this.changeDetector.detectChanges();
    }
  }

  async importPlayers(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.isImporting) {
      return;
    }

    try {
      this.isImporting = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.importMessage = '';
      this.importErrors = [];
      const result = await this.playerRegistryService.importFile(file);
      this.players = result.players;
      this.currentPage = 1;
      this.importErrors = result.errors ?? [];
      this.importMessage = `Import completato: ${result.created} creati, ${result.updated} aggiornati, ${result.skipped} scartati.`;
      this.changed.emit();
      this.changeDetector.detectChanges();
    } catch (error) {
      this.importMessage = '';
      this.importErrors = [];
      this.errorMessage = this.readError(error, 'Errore durante import anagrafica.');
      this.changeDetector.detectChanges();
    } finally {
      this.isImporting = false;
      this.changeDetector.detectChanges();
    }
  }

  openCreatePlayerModal(): void {
    this.resetPlayerForm();
    this.playerModalOpen = true;
    this.successMessage = '';
    this.changeDetector.detectChanges();
  }

  closePlayerModal(): void {
    this.playerModalOpen = false;
    this.resetPlayerForm();
  }

  resetPlayerForm(): void {
    this.editingPlayer = null;
    this.playerForm = this.emptyPlayerForm();
    this.errorMessage = '';
    this.changeDetector.detectChanges();
  }

  playerLabel(player: PlayerRegistryEntry): string {
    return `${player.last_name} ${player.first_name} (${player.nickname})`;
  }

  contactLabel(player: PlayerRegistryEntry): string {
    return [player.phone, player.email].filter(Boolean).join(' | ');
  }

  roleLabel(role: PlayerRole | null | undefined): string {
    return role || '-';
  }

  isDeletingPlayer(playerId: number): boolean {
    return this.deletingPlayerIds.has(playerId);
  }

  onFiltersChange(): void {
    this.currentPage = 1;
    this.changeDetector.detectChanges();
  }

  clearFilters(): void {
    this.filters = {
      firstName: '',
      lastName: '',
      nickname: ''
    };
    this.currentPage = 1;
    this.changeDetector.detectChanges();
  }

  changePage(page: number): void {
    this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    this.changeDetector.detectChanges();
  }

  changePageSize(): void {
    this.pageSize = Math.max(5, Number(this.pageSize || 10));
    this.currentPage = 1;
    this.changeDetector.detectChanges();
  }

  private emptyPlayerForm(): Partial<PlayerRegistryEntry> {
    return {
      first_name: '',
      last_name: '',
      nickname: '',
      jersey_number: null,
      role: 'Portiere',
      age: null,
      phone: null,
      email: null
    };
  }

  private readError(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
  }

  private normalizeFilter(value: string | null | undefined): string {
    return (value ?? '').trim().toLocaleLowerCase('it-IT');
  }

  private isValidRole(role: PlayerRole | null | undefined): role is PlayerRole {
    return this.roleOptions.includes(role as PlayerRole);
  }

  private normalizeCurrentPage(): void {
    this.currentPage = Math.min(this.currentPage, this.totalPages);
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }
}
